import type { FC } from 'react';
import type React from 'react';
import { Fragment, useRef } from 'react';
import DropdownChevron from '@components/DropdownChevron';
import IconMdi from '@components/IconMdi';
import { Show, ShowFirstMatching } from '@components/Show';
import { Spinner } from '@components/Spinner';
import { TooltipedIcon } from '@components/TooltipedIcon';
import { PORTALLED_SELECT_MENU_Z_INDEX } from '@constants/z-indices';
import { autoUpdate, FloatingPortal, hide, offset, shift, size, useFloating } from '@floating-ui/react';
import { mdiCloseCircleOutline, mdiMagnify } from '@mdi/js';
import { useEffectOnce } from '@src/hooks';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import type { DownshiftState, StateChangeOptions } from 'downshift';
import Downshift from 'downshift';
import { FocusScope } from 'radix-ui/internal';
import { twMerge } from 'tailwind-merge';

import type { UIComponentBase, UIOnChangeFn, UISelectionType } from '../../@types/types';

import { selectTriggerSize } from './classes/select';
import type { InputFieldProps } from './InputField';
import { InputField } from './InputField';
import InputReadOnly from './InputReadOnly';
import { MAX_DROPDOWN_HEIGHT_PX } from './shared';

type InputSelectStyle = 'button' | 'input' | 'menuInner' | 'menuItem' | 'menuOuter' | 'menuItemDisabled';
type InputSelectStyles = Record<InputSelectStyle, string>;

declare module 'react' {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactNode | null,
  ): (props: P & React.RefAttributes<T>) => React.ReactNode | null;
}

export const inputSelectStyles: InputSelectStyles = {
  button:
    'bg-white border border-info-300 px-3 py-2 rounded-sm text-info-900 h-10 flex justify-between items-center w-full focus:outline-primary-500',
  input: 'border-b border-info-100 px-3 py-2 text-info-900 w-full focus:outline-hidden',
  menuInner: 'overflow-y-auto list-none',
  menuItem: 'py-2 px-3 cursor-pointer text-sm font-sans truncate',
  menuOuter:
    'border-info-300 border rounded-sm absolute inset-x-0 z-1200 top-0 bg-white text-info-900 shadow-sm flex flex-col overflow-y-auto min-w-fit w-[184px]',
  menuItemDisabled: 'cursor-not-allowed bg-info-50 text-info-300',
};

const itemClasses = cva(inputSelectStyles.menuItem, {
  variants: {
    isEmpty: { true: 'text-info-400' },
    isDisabled: { true: inputSelectStyles.menuItemDisabled },
    isHighlighted: { true: 'bg-info-100' },
  },
});

const selectTriggerClasses = cva(['m-auto flex flex-1 justify-between rounded-md text-sm text-info-700 items-center'], {
  variants: {
    unstyled: {
      true: '',
      false: 'border border-info-300 shadow-xs',
    },
    isDisabled: {
      true: 'cursor-not-allowed opacity-50',
      false: '',
    },
    isIncomplete: { true: 'field-incomplete', false: '' },
  },
  defaultVariants: {
    unstyled: false,
    isDisabled: false,
    isIncomplete: false,
  },
});

export interface InputSelectProps<T = string> extends InputFieldProps {
  'data-testid'?: string;
  id?: string;
  isClearable?: boolean;
  isFetchingOptions?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isSearchable?: boolean;
  labelText?: string;
  name?: string;
  options?: readonly UISelectionType<T>[] | readonly string[];
  value?: T;
  initialSelectedItem?: T;
  placeholder?: string;
  placeholderClassName?: string;
  'aria-labelledby'?: string;
  'aria-label'?: string;
  formatter?: (value: any) => string | number | false;
  itemFormatter?: (value: any) => string;
  onChange?: UIOnChangeFn<T>;
  menuOuterClassName?: string;
  unstyled?: boolean;
  limitValueLengthPx?: number;
  inputClassName?: string;
  shouldRenderInsidePortal?: boolean;
}

export interface MenuContainerProps {
  anchorRef?: React.RefObject<HTMLElement | null>;
  children?: UIComponentBase['children'];
  className?: string;
  offsetY?: number;
  shouldRenderInsidePortal?: boolean;
}

export type UseDownshiftStyleState = React.CSSProperties | undefined;
const downshiftStateReducer = <T extends number | string | null | undefined = string>(
  _state: DownshiftState<UISelectionType<T>>,
  changes: StateChangeOptions<UISelectionType<T>>,
): Partial<StateChangeOptions<UISelectionType<T>>> => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.keyDownEnter:
    case Downshift.stateChangeTypes.clickItem:
      return { ...changes, inputValue: '' };

    case Downshift.stateChangeTypes.clickButton:
    case Downshift.stateChangeTypes.changeInput:
      return { ...changes, highlightedIndex: 0 };

    default:
      return changes;
  }
};

// Portalled menus escape the modal's clipping box: above MODAL_Z_INDEX and free of
// menuOuter's inset-x-0, which would stretch them across the portal root (twMerge drops both).
const PORTAL_MENU_CLASSES = `${PORTALLED_SELECT_MENU_Z_INDEX} inset-x-auto`;

const WHEEL_LINE_TO_PX = 16;

// Modal scroll locks preventDefault wheel over body-portalled menus. Capture-phase locks show up here as
// defaultPrevented; react-remove-scroll (radix dialogs) prevents later at document — data-scroll-locked on <body> is its tell.
const handleScrollLockedWheel = (event: React.WheelEvent<HTMLDivElement>) => {
  if (!event.defaultPrevented && !document.body.hasAttribute('data-scroll-locked')) return;
  const deltaY = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? event.deltaY * WHEEL_LINE_TO_PX : event.deltaY;
  for (
    let node = event.target as HTMLElement | null;
    node && event.currentTarget.contains(node);
    node = node.parentElement
  ) {
    if (node.scrollHeight > node.clientHeight) {
      node.scrollTop += deltaY;
      return;
    }
  }
};

export const InputSelectMenuContainer: FC<MenuContainerProps> = ({
  anchorRef,
  children,
  className = inputSelectStyles.menuOuter,
  offsetY = 4,
  shouldRenderInsidePortal = false,
}) => {
  const { refs, floatingStyles, middlewareData } = useFloating({
    placement: 'bottom-start',
    open: true,
    middleware: [
      offset({ mainAxis: offsetY }),
      shift(),
      size({
        padding: 8,
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
      hide(),
    ],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: anchorRef?.current,
    },
  });

  const ParentContainer = shouldRenderInsidePortal ? FloatingPortal : Fragment;

  const focusSearchInput = (root: HTMLElement | null) =>
    root?.querySelector<HTMLInputElement>('input:not([type="hidden"])')?.focus();

  useEffectOnce(() => {
    if (!shouldRenderInsidePortal) focusSearchInput(refs.floating.current);
  });

  // Hide via visibility (not close — downshift reopens on scroll-back) when `hide()` flags the trigger clipped,
  // else the body-portalled menu floats over the sticky header. Portalled-only: jsdom flags referenceHidden spuriously.
  const hideOnScroll = shouldRenderInsidePortal && middlewareData.hide?.referenceHidden;

  const menu = (
    <div
      data-slot="input-select-menu"
      className={twMerge(className, shouldRenderInsidePortal && PORTAL_MENU_CLASSES)}
      ref={refs.setFloating}
      onWheel={shouldRenderInsidePortal ? handleScrollLockedWheel : undefined}
      style={{
        ...floatingStyles,
        visibility: hideOnScroll ? 'hidden' : floatingStyles.visibility,
        ...(shouldRenderInsidePortal && { pointerEvents: 'auto' }),
      }}
    >
      {children}
    </div>
  );

  // Portalled focus waits for the FocusScope mount to pause a surrounding dialog's trap (Radix
  // scope stack); earlier focus gets yanked, and the transient blur makes downshift close the menu.
  const handleMountAutoFocus = (event: Event) => {
    event.preventDefault();
    focusSearchInput(event.currentTarget as HTMLElement | null);
  };

  return (
    <ParentContainer>
      {shouldRenderInsidePortal ? (
        <FocusScope.Root asChild loop={false} trapped={false} onMountAutoFocus={handleMountAutoFocus}>
          {menu}
        </FocusScope.Root>
      ) : (
        menu
      )}
    </ParentContainer>
  );
};

const formatSelectValue = <T = string,>(value: T, options: UISelectionType<T>[] = []) => {
  const selected = options.find((option) => option.value === value);
  return selected?.name ?? value;
};

const getItemToString = <T = string,>(item: UISelectionType<T> | null): string => {
  return (item?.value ?? '').toString();
};

const parseInputOptions = <T = string,>(
  options: readonly UISelectionType<T>[] | readonly string[] = [],
): UISelectionType<T>[] => {
  return options.map((item) => (typeof item === 'string' ? ({ name: item, value: item } as UISelectionType<T>) : item));
};

const InputSelect = <T extends string | number = string>({
  dataTestId,
  className,
  descriptionText,
  errors,
  formatter,
  id,
  inputClassName,
  isClearable = false,
  isFetchingOptions = false,
  isIncomplete = false,
  isLoading = false,
  isReadOnly,
  isSearchable = true,
  itemFormatter,
  labelText,
  limitValueLengthPx,
  menuOuterClassName,
  name = 'input',
  onChange,
  options: defaultOptions,
  placeholder = 'Select',
  placeholderClassName,
  shouldRenderInsidePortal,
  size = 'md',
  unstyled,
  value,
  ...props
}: InputSelectProps<T>) => {
  const anchorRef = useRef<HTMLElement>(null);
  const options = parseInputOptions(defaultOptions);
  const getReadOnlyItem = (value?: T) => {
    const selected = options?.find((option) => option.value === value);
    return selected?.name ?? value;
  };

  const handleRemoveSelection: React.HTMLAttributes<HTMLDivElement>['onClick'] = (event) => {
    event.stopPropagation();
    return onChange?.(undefined, name);
  };

  return (
    <InputField
      className={className}
      descriptionText={descriptionText}
      errors={errors}
      id={id}
      labelText={labelText}
      name={name}
      {...props}
    >
      {isReadOnly && (
        <InputReadOnly
          {...props}
          aria-describedby={descriptionText ? `${name}Description` : undefined}
          name={name}
          value={getReadOnlyItem(value) || ''}
        />
      )}

      {!isReadOnly && (
        <Downshift<UISelectionType<T>>
          stateReducer={downshiftStateReducer}
          // @ts-expect-error
          selectedItem={value ?? ''}
          itemToString={getItemToString}
          onSelect={(item) => {
            if (onChange) {
              onChange(item?.value, name);
            }
          }}
        >
          {({
            getItemProps,
            getMenuProps,
            getToggleButtonProps,
            highlightedIndex,
            isOpen,
            inputValue,
            getInputProps,
          }) => {
            const normalized = String(inputValue ?? '').toLowerCase();
            const byInputValue = (item: UISelectionType<T>) =>
              String(item.name ?? '')
                .toLowerCase()
                .includes(normalized);
            const filteredItems = options?.filter(byInputValue);
            const selectableItems = isSearchable ? filteredItems : options;
            const hasSelection = Boolean(value);

            return (
              <div className="relative flex text-sm">
                <button
                  id={id ? `${id}-select-trigger` : undefined}
                  data-testid={`${dataTestId}:select-trigger`}
                  disabled={props.isDisabled}
                  {...getToggleButtonProps()}
                  aria-label={labelText ?? props['aria-label']}
                  aria-labelledby={props['aria-labelledby']}
                  type="button"
                  ref={anchorRef}
                  className={clsx(
                    selectTriggerClasses({
                      unstyled,
                      isDisabled: props.isDisabled || isLoading,
                      className: inputClassName,
                      isIncomplete,
                    }),
                    selectTriggerSize({ size }),
                  )}
                >
                  {hasSelection ? (
                    <span
                      style={{
                        maxWidth: limitValueLengthPx,
                      }}
                      className={clsx(limitValueLengthPx && 'truncate whitespace-nowrap')}
                    >
                      {formatter ? formatter(value) : formatSelectValue(value, options)}
                    </span>
                  ) : (
                    <span className={clsx('text-info-400', placeholderClassName)}>{placeholder}</span>
                  )}
                  <div className="my-auto inline-flex">
                    {isLoading && <Spinner className="[&>path]:fill-info-500" size="xs" />}
                    {isClearable && hasSelection && (
                      <TooltipedIcon
                        data-testid={dataTestId ? `${dataTestId}:clear` : 'clear'}
                        placement="top"
                        path={mdiCloseCircleOutline}
                        className="text-info-400 hover:text-error-500 cursor-pointer transition-colors"
                        size={0.625}
                        content="Remove selection"
                        onClick={handleRemoveSelection}
                      />
                    )}
                    <Show when={!props.isDisabled}>
                      <DropdownChevron open={isOpen} />
                    </Show>
                  </div>
                </button>

                {isOpen && (
                  <InputSelectMenuContainer
                    anchorRef={anchorRef}
                    className={clsx(inputSelectStyles.menuOuter, menuOuterClassName)}
                    shouldRenderInsidePortal={shouldRenderInsidePortal}
                  >
                    <div
                      {...getMenuProps({}, { suppressRefError: shouldRenderInsidePortal })}
                      className="flex min-h-0 flex-col"
                      data-testid={`${dataTestId}:downshift-list`}
                    >
                      {isSearchable && (
                        <div className="relative">
                          <IconMdi
                            path={mdiMagnify}
                            size={0.75}
                            className="text-info-400 absolute top-1/2 left-2 -translate-y-1/2"
                          />
                          <input
                            className={clsx(inputSelectStyles.input, 'pl-8')}
                            placeholder="Search..."
                            {...getInputProps({ disabled: isFetchingOptions })}
                          />
                        </div>
                      )}

                      <ShowFirstMatching>
                        <Show when={isFetchingOptions}>
                          <ul
                            className={clsx(inputSelectStyles.menuInner)}
                            style={{ maxHeight: MAX_DROPDOWN_HEIGHT_PX }}
                          >
                            <li className={itemClasses()}>
                              <p className="h-3 w-full animate-pulse rounded-xl bg-slate-200" />
                            </li>
                            <li className={itemClasses()}>
                              <p className="h-3 w-full animate-pulse rounded-xl bg-slate-200" />
                            </li>
                            <li className={itemClasses()}>
                              <p className="h-3 w-full animate-pulse rounded-xl bg-slate-200" />
                            </li>
                            <li className={itemClasses()}>
                              <p className="h-3 w-full animate-pulse rounded-xl bg-slate-200" />
                            </li>
                          </ul>
                        </Show>
                        <Show when={!isFetchingOptions}>
                          <ul className={inputSelectStyles.menuInner} style={{ maxHeight: MAX_DROPDOWN_HEIGHT_PX }}>
                            {(selectableItems || []).map((item, index) => {
                              const { isDisabled = false } = item;
                              const isEmpty = item.value === undefined;
                              const result =
                                (itemFormatter && itemFormatter(item.value)) ||
                                (formatter && formatter(item.value)) ||
                                item.name;

                              const { onClick, onMouseDown, onMouseMove, ...itemProps } = getItemProps({ item });

                              const actionProps = isDisabled
                                ? {}
                                : {
                                    onClick,
                                    onMouseDown,
                                    onMouseMove,
                                  };

                              return (
                                <li
                                  data-testid={dataTestId ? `${dataTestId}:option-${item.name}` : `option-${item.name}`}
                                  className={itemClasses({
                                    isEmpty,
                                    isDisabled: isDisabled && !isEmpty,
                                    isHighlighted: index === highlightedIndex,
                                  })}
                                  key={item.value ? JSON.stringify(item.value) : `select-${id}-${index}`}
                                  {...actionProps}
                                  {...itemProps}
                                >
                                  {result}
                                </li>
                              );
                            })}
                          </ul>
                        </Show>
                      </ShowFirstMatching>
                    </div>
                  </InputSelectMenuContainer>
                )}
              </div>
            );
          }}
        </Downshift>
      )}
    </InputField>
  );
};

export default InputSelect;
